import NotificationStatus from '@/domains/common/typescript/NotificationStatus';
import useSettingsStore from '@/domains/settings/store/settings.store';
import scanApi from '@/domains/scan/api/scan.api';
import eventBus from '@/domains/common/services/eventBus';
import isPodonexReady from '@/domains/common/services/isPodonexReady';
import { isMessage } from '@/domains/scan/services/typeGuards';
import { defineStore, storeToRefs } from 'pinia';
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type ScanFilesBlobInterface from '@/domains/scan/typescript/interfaces/ScanFilesBlobInterface';
import type DeviceInterface from '@/domains/scan/typescript/interfaces/DeviceInterface';

export default defineStore('3d', () => {
    const { t } = useI18n();
    const settingsStore = useSettingsStore();

    const { podonexConfiguration } = storeToRefs(settingsStore);

    const isScanInProcess = ref<boolean>(false);
    const isScanLoading = ref<boolean>(false);
    const podonexScannerData = ref<DeviceInterface>();

    const original2DScanLeft = ref<{ image: Blob; dpi: number }>();
    const left2DScanNoBg = ref<Blob>();
    const original2DScanRight = ref<{ image: Blob; dpi: number }>();
    const right2DScanNoBg = ref<Blob>();

    function stopPollingScan() {
        eventBus.emit('stop-polling-scan');
    }

    async function fetchScannerInfo(): Promise<void> {
        if (podonexConfiguration.value === undefined || !podonexConfiguration.value.scannerId) {
            return;
        }
        const isPodonexReadyToUse = await isPodonexReady();

        if (!isPodonexReadyToUse) {
            return;
        }

        try {
            await scanApi.cancelButtonScan();
            const deviceInfo = await scanApi.getDeviceInfo(podonexConfiguration.value.scannerId);
            if (isMessage(deviceInfo) || deviceInfo === undefined) {
                eventBus.emit('notification-message', {
                    title: t('scan.scanner.scanner_not_available'),
                    status: NotificationStatus.error,
                });
                return;
            }
            podonexScannerData.value = deviceInfo;
        } catch {
            eventBus.emit('notification-message', {
                title: t('api.scanner_not_ready'),
                status: NotificationStatus.error,
            });
        }
    }

    const scan = reactive<ScanFilesBlobInterface>({
        file: new Blob(),
        fileType: 'jpg',
    });

    function $reset() {
        console.info('reset 3D store');
        scan.file = new Blob();
        scan.fileType = 'jpg';
        original2DScanLeft.value = undefined;
        left2DScanNoBg.value = undefined;
        original2DScanRight.value = undefined;
        right2DScanNoBg.value = undefined;
        podonexScannerData.value = undefined;
        isScanInProcess.value = false;
        // resetAllStores() relies on this function to leave nothing behind, loaders included: a
        // logout mid-load used to carry a spinner into the next session.
        isScanLoading.value = false;
    }

    return {
        scan,
        fetchScannerInfo,
        podonexScannerData,
        isScanInProcess,
        isScanLoading,
        original2DScanLeft,
        left2DScanNoBg,
        original2DScanRight,
        right2DScanNoBg,
        stopPollingScan,
        $reset,
    };
});
